import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { Dropdown } from "semantic-ui-react";
import apis from "../../../../apis";
import { getDateFormat } from "../../../../common/date-format";
import { UsersContext } from "../../../../common/UsersContext";

import "./BotMonitoring.styles.scss";

const BOT_NAMES = {
  esta: "ESTA",
  ds160: "VISA",
  "esta-status-checker": "ESTA status checker",
  ds82: "DS82",
};

const BOT_STATUS_COLOR = {
  ok: "green",
  warning: "orange",
  error: "red",
};

const DS82_POLL_MS = 45 * 1000;

const getDs82Color = (status) => {
  if (!status) return BOT_STATUS_COLOR.ok;
  if (status.autoPaused) return BOT_STATUS_COLOR.error;
  if (status.manualPaused) return BOT_STATUS_COLOR.warning;
  return BOT_STATUS_COLOR.ok;
};

const resolveUserName = (userId, allUsers) => {
  if (!userId) return null;
  const user = allUsers.find((u) => u._id === userId);
  return user?.fullName || user?.username || user?.email || "Unknown";
};

const getDs82Subtitle = (status, allUsers) => {
  if (!status) return "";
  if (status.autoPaused) {
    return status.firstSeenZeroAt
      ? `ECS=0 since ${getDateFormat(status.firstSeenZeroAt)}`
      : "ECS=0 (auto-paused)";
  }
  if (status.manualPaused) {
    if (!status.lastPauseChangeAt) return "Manually paused";
    const userName = resolveUserName(status.lastPauseChangeBy, allUsers);
    const suffix = userName ? ` by ${userName}` : "";
    return `Manually paused at ${getDateFormat(status.lastPauseChangeAt)}${suffix}`;
  }
  const count = status.ecsRunningCount ?? "?";
  return `${count} running`;
};

const BotMonitoring = () => {
  const [botStatuses, setBotStatuses] = useState([]);
  const [ds82Status, setDs82Status] = useState(null);
  const loggedIn = useSelector((state) => state.auth.loggedIn);
  const { allUsers = [] } = useContext(UsersContext) || {};
  const isMountedRef = useRef(true);
  const loggedInRef = useRef(loggedIn);

  useEffect(() => {
    loggedInRef.current = loggedIn;
  }, [loggedIn]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const shouldApplyFetch = () => isMountedRef.current && loggedInRef.current;

  const fetchBotMonitoring = useCallback(async () => {
    try {
      const { data } = await apis.botMonitoring.getBotMonitoring();
      if (shouldApplyFetch()) setBotStatuses(data);
    } catch (error) {
      console.error(error);
      if (shouldApplyFetch()) setBotStatuses([]);
    }
  }, []);

  const fetchDs82Status = useCallback(async () => {
    try {
      const { data } = await apis.botMonitoring.getDs82BotStatus();
      if (shouldApplyFetch()) setDs82Status(data);
    } catch {
      if (shouldApplyFetch()) setDs82Status(null);
    }
  }, []);

  useEffect(() => {
    if (loggedIn) {
      fetchBotMonitoring();
    } else {
      setBotStatuses([]);
    }
  }, [loggedIn, fetchBotMonitoring]);

  useEffect(() => {
    if (!loggedIn) {
      setDs82Status(null);
      return undefined;
    }
    fetchDs82Status();
    const id = setInterval(fetchDs82Status, DS82_POLL_MS);
    return () => clearInterval(id);
  }, [loggedIn, fetchDs82Status]);

  return (
    <div className="dropdown">
      <Dropdown text="Gov Site Status" button icon="bug">
        <Dropdown.Menu>
          <div className="bot-status">
            {botStatuses.map(
              ({ botName, botStatus, statusMessage, updatedAt }) => (
                <Dropdown.Item
                  key={botName}
                  className="bot-status__dropdown-item"
                >
                  <div className="bot-status__item">
                    <div className="bot-status__item__title">
                      <span
                        className="bot-status__item__title__status"
                        style={{
                          backgroundColor: BOT_STATUS_COLOR[botStatus],
                        }}
                      />
                      <span>{BOT_NAMES[botName]}</span>
                      <span style={{ marginLeft: "auto" }}>
                        {getDateFormat(updatedAt)}
                      </span>
                    </div>
                    <div
                      className="bot-status__item__content"
                      style={{ color: BOT_STATUS_COLOR[botStatus] }}
                    >
                      {statusMessage}
                    </div>
                  </div>
                </Dropdown.Item>
              ),
            )}
            {ds82Status && (
              <Dropdown.Item
                key="ds82"
                className="bot-status__dropdown-item"
              >
                <div className="bot-status__item">
                  <div className="bot-status__item__title">
                    <span
                      className="bot-status__item__title__status"
                      style={{ backgroundColor: getDs82Color(ds82Status) }}
                    />
                    <span>{BOT_NAMES.ds82}</span>
                    <span style={{ marginLeft: "auto" }}>
                      {ds82Status.lastEcsCheckAt &&
                        getDateFormat(ds82Status.lastEcsCheckAt)}
                    </span>
                  </div>
                  <div
                    className="bot-status__item__content"
                    style={{ color: getDs82Color(ds82Status) }}
                  >
                    {getDs82Subtitle(ds82Status, allUsers)}
                  </div>
                </div>
              </Dropdown.Item>
            )}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default BotMonitoring;
